// Updated SocialPostsView.js - With connection status awareness and UX improvements

import React, { useEffect, useState, useCallback } from "react";
import { apiGet, apiPost } from "../utils/api";
import { fetchAuthSession } from 'aws-amplify/auth';
import SchedulePostDialog from "./SchedulePostDialog";
import ApprovalDropdown from "./ApprovalDropdown";
import AdditionalSocialPostActions from "./AdditionalSocialPostActions";

const SocialPostsView = ({ podcastId, onBatchApprovalReady }) => {
  const [posts, setPosts] = useState([]);
  const [, setAllPosts] = useState([]);
  const [editingPost, setEditingPost] = useState(null);
  const [editContent, setEditContent] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showScheduleDialog, setShowScheduleDialog] = useState(false);
  const [postToSchedule, setPostToSchedule] = useState(null);
  const [reSmackLoading, setReSmackLoading] = useState({});
  const [isBatchApproving, setIsBatchApproving] = useState(false);
  const [calculatedScheduleDates, setCalculatedScheduleDates] = useState({});
  
  // NEW: Connection status tracking
  const [connectionStatuses, setConnectionStatuses] = useState({
    facebook: { connected: false },
    instagram: { connected: false },
    linkedin: { connected: false },
    x: { connected: false }
  });
  const [feedId, setFeedId] = useState(null);

  // NEW: Extract feedId from podcastId
  useEffect(() => {
    if (podcastId) {
      const parts = podcastId.split('#');
      // Format: userId#feedId#episodeId
      if (parts.length >= 2) {
        const extractedFeedId = parts.length >= 3 ? parts[1] : parts[0];
        setFeedId(extractedFeedId);
      }
    }
  }, [podcastId]);

  // NEW: Fetch connection statuses for the feed
  const fetchConnectionStatuses = useCallback(async () => {
    if (!feedId) return;
    
    try {
      const session = await fetchAuthSession();
      const userId = session.tokens?.idToken?.payload?.sub;

      const data = await apiPost('/connections/status', { userId, feedId });

      // Transform the response to our local format
      setConnectionStatuses({
          facebook: { 
            connected: data.facebook?.connected || false,
            accountName: data.facebook?.accountName 
          },
          instagram: { 
            connected: data.instagram?.connected || false,
            accountName: data.instagram?.accountName 
          },
          linkedin: { 
            connected: data.linkedin?.connected || false,
            accountName: data.linkedin?.accountName 
          },
          x: {
            connected: data.x?.connected || false,
            accountName: data.x?.accountName
          }
        });
    } catch (error) {
      console.error("Error fetching connection statuses:", error);
    }
  }, [feedId]);

  // Fetch connection statuses when feedId is available
  useEffect(() => {
    if (feedId) {
      fetchConnectionStatuses();
    }
  }, [feedId, fetchConnectionStatuses]);

  const fetchSocialPosts = useCallback(async (silent = false) => {
    try {
      if (isBatchApproving) {
        return;
      }
      if (!silent) setLoading(true);

      const episodeId = podcastId.split('#').pop();
      const response = await apiGet(`/getAllSocialPosts`, { episodeId });

      const exactPodcastMatch = response.posts ? response.posts.filter(post => post.podcastId === podcastId) : [];
      setPosts(exactPodcastMatch.length > 0 ? exactPodcastMatch : (response.posts || []));
      setError(null);
    } catch (error) {
      console.error("Error fetching social posts:", error);
      setError("Failed to load social posts. Please try again later.");
    } finally {
      if (!silent) setLoading(false);
    }
  }, [podcastId, isBatchApproving]);

  const fetchAllPosts = useCallback(async () => {
    try {
      const response = await apiGet('/getAllSocialPosts');
      setAllPosts(response.posts || []);
    } catch (error) {
      console.error("Error fetching all posts:", error);
    }
  }, []);

  /**
   * Get all scheduled dates for a specific feed and platform
   * Returns array of date timestamps (normalized to midnight) that are already scheduled
   */
  const getScheduledDatesForFeedAndPlatform = useCallback(async (podcastId, platform) => {
    try {
      // Extract feedId from podcastId (format: userId#feedId#episodeId)
      const parts = podcastId.split('#');
      const feedId = parts.length >= 2 ? parts[1] : null;
      
      if (!feedId) {
        console.warn('Could not extract feedId from podcastId:', podcastId);
        return [];
      }
      
      // Fetch all scheduled posts for this feed + platform combination
      const response = await apiGet('/getAllSocialPosts', { 
        feedId: feedId,
        platform: platform,
        status: 'scheduled'
      });
      
      const scheduledPosts = response.posts || [];
      
      // Extract and normalize dates to midnight for day-level comparison
      const scheduledDates = scheduledPosts
        .filter(post => post.scheduledTime)
        .map(post => {
          const date = new Date(post.scheduledTime);
          date.setHours(0, 0, 0, 0);
          return date.getTime();
        });
      
      console.log(`📅 Found ${scheduledDates.length} scheduled dates for feed ${feedId}, platform ${platform}`);
      
      return scheduledDates;
    } catch (error) {
      console.error('Error fetching scheduled dates:', error);
      return [];
    }
  }, []);

  useEffect(() => {
    if (podcastId) {
      fetchSocialPosts();
      fetchAllPosts();
    }
  }, [podcastId, fetchSocialPosts, fetchAllPosts]);

  useEffect(() => {
    const pendingCount = posts.filter(post => post.status === 'pending').length;
    
    // Call parent callback if provided
    if (onBatchApprovalReady) {
      onBatchApprovalReady({
        hasPendingPosts: pendingCount > 0,
        pendingCount: pendingCount,
        batchApprove: handleBatchApprove,
        isBatchApproving: isBatchApproving
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [posts, isBatchApproving, onBatchApprovalReady]);

  // Calculate smart scheduled dates for all pending posts
  useEffect(() => {
    const calculateSmartDates = async () => {
      if (posts.length === 0) return;
      
      const pendingPosts = posts.filter(p => p.status === 'pending');
      const dateCalculations = {};
      
      for (const post of pendingPosts) {
        const platform = post.provider || post.providerKey?.split('#')[0];
        const variantNumber = post.variantNumber || 1;
        const postKey = post.providerKey || post.provider;
        
        // Get existing scheduled dates for this feed + platform
        const existingDates = await getScheduledDatesForFeedAndPlatform(post.podcastId, platform);
        
        // Calculate smart date
        const smartDate = getSmartScheduledDate(variantNumber, existingDates);
        
        dateCalculations[postKey] = smartDate;
      }
      
      setCalculatedScheduleDates(dateCalculations);
    };
    
    calculateSmartDates();
  }, [posts, getScheduledDatesForFeedAndPlatform]);

  const startEditing = (post) => {
    setEditingPost(post);
    setEditContent(post.content);
  };

  const cancelEditing = () => {
    setEditingPost(null);
    setEditContent("");
  };

  const updatePost = async (post, action, additionalData = {}, optimisticData = null) => {
    try {
      const providerValue = post.providerKey ||
        `${post.providerPlatform || post.provider}#${post.variantNumber || 1}`;

      let updateData = {
        podcastId: post.podcastId,
        provider: providerValue,
        ...additionalData
      };

      if (action === "edit") {
        updateData.content = editContent;
      }

      // Apply optimistic update immediately so the card updates without a loading flash
      if (optimisticData) {
        setPosts(prev => prev.map(p => {
          const pKey = p.providerKey || `${p.podcastId}-${p.provider}`;
          const postKey = post.providerKey || `${post.podcastId}-${post.provider}`;
          return pKey === postKey ? { ...p, ...optimisticData } : p;
        }));
      }

      await apiPost('/updateSocialPost', updateData);
      await fetchSocialPosts(!!optimisticData); // silent if we already updated optimistically

      if (action === "edit") {
        cancelEditing();
      }
    } catch (error) {
      console.error(`Error ${action}ing post:`, error);
      setError(`Failed to ${action} post.`);
      await fetchSocialPosts(); // revert on error
    }
  };

  const handleBatchApprove = async () => {
    try {
      setIsBatchApproving(true);
      const pendingPosts = posts.filter(post => post.status === 'pending');

      // Optimistically update all pending posts at once
      const optimisticUpdates = {};
      for (const post of pendingPosts) {
        const postKey = post.providerKey || post.provider;
        const calculatedDate = calculatedScheduleDates[postKey];
        if (calculatedDate) {
          optimisticUpdates[postKey] = {
            status: 'scheduled',
            scheduledTime: calculatedDate.toISOString(),
            isApproved: true
          };
        }
      }
      setPosts(prev => prev.map(p => {
        const key = p.providerKey || p.provider;
        return optimisticUpdates[key] ? { ...p, ...optimisticUpdates[key] } : p;
      }));

      for (const post of pendingPosts) {
        const postKey = post.providerKey || post.provider;
        const calculatedDate = calculatedScheduleDates[postKey];
        if (calculatedDate) {
          await updatePost(post, 'approve', {
            scheduledTime: calculatedDate.toISOString(),
            isApproved: true,
            status: 'scheduled'
          });
        }
      }

      await fetchSocialPosts(true); // silent — cards already updated
      await fetchAllPosts();
    } catch (error) {
      console.error("Error batch approving posts:", error);
      setError("Failed to approve all posts. Some posts may have been approved successfully.");
      await fetchSocialPosts(); // revert on error
    } finally {
      setIsBatchApproving(false);
    }
  };

  const handlePostNow = async (post, variantNumber) => {
    const postKey = post.providerKey || post.provider;
    const calculatedDate = calculatedScheduleDates[postKey];
    const effectiveVariant = variantNumber || post.variantNumber || 1;

    let additionalData;
    let scheduledTime;
    if (effectiveVariant === 1) {
      scheduledTime = new Date().toISOString();
      additionalData = {
        scheduledTime,
        isApproved: true,
        status: 'scheduled',
        immediatePosting: true
      };
    } else {
      scheduledTime = calculatedDate ? calculatedDate.toISOString() : new Date().toISOString();
      additionalData = {
        scheduledTime,
        isApproved: true,
        status: 'scheduled'
      };
    }

    const optimisticData = { status: 'scheduled', scheduledTime, isApproved: true };
    await updatePost(post, 'postNow', additionalData, optimisticData);
    await fetchAllPosts();
  };

  const handleCustomSchedule = (post) => {
    setPostToSchedule(post);
    setShowScheduleDialog(true);
  };

  const handleSchedulePost = async (scheduledTime) => {
    if (!postToSchedule) return;

    try {
      const optimisticData = {
        status: 'scheduled',
        scheduledTime: scheduledTime.toISOString(),
        isApproved: true
      };
      await updatePost(postToSchedule, 'schedule', {
        scheduledTime: scheduledTime.toISOString(),
        isApproved: true,
        status: 'scheduled'
      }, optimisticData);

      setShowScheduleDialog(false);
      setPostToSchedule(null);
      await fetchAllPosts();
    } catch (error) {
      console.error("Error scheduling post:", error);
      setError("Failed to schedule post. Please try again.");
    }
  };

  const handleReSmack = async (post) => {
    const loadingKey = post.providerKey || `${post.provider}-${post.podcastId}`;
    
    try {
      setReSmackLoading(prev => ({ ...prev, [loadingKey]: true }));
      setError(null);
      
      // Construct provider key - prefer providerKey, reconstruct if missing
      const providerValue = post.providerKey ||
        `${post.providerPlatform || post.provider}#${post.variantNumber || 1}`;

      await apiPost('/regenerateSocialPost', {
        podcastId: post.podcastId,
        provider: providerValue
      });
      
      await fetchSocialPosts();
    } catch (error) {
      console.error("Error re-generating post:", error);
      setError("Failed to re-generate post. Please try again.");
    } finally {
      setReSmackLoading(prev => ({ ...prev, [loadingKey]: false }));
    }
  };

  const handleArchive = async (post) => {
    try {
      await updatePost(post, 'archive', { status: 'archived' });
      setError(null);
    } catch (error) {
      console.error("Error archiving post:", error);
      setError("Failed to archive post. Please try again.");
    }
  };

  const handleUnarchive = async (post) => {
    try {
      await updatePost(post, 'unarchive', { status: 'pending' });
      setError(null);
    } catch (error) {
      console.error("Error unarchiving post:", error);
      setError("Failed to unarchive post. Please try again.");
    }
  };

  const handleRepostNow = async (post) => {
    try {
      const now = new Date();

      // Construct provider key - prefer providerKey, reconstruct if missing
      const providerValue = post.providerKey ||
        `${post.providerPlatform || post.provider}#${post.variantNumber || 1}`;

      const updateData = {
        podcastId: post.podcastId,
        provider: providerValue,
        scheduledTime: now.toISOString(),
        isApproved: true,
        status: 'scheduled',
        immediatePosting: true
      };

      await apiPost('/updateSocialPost', updateData);
      await fetchSocialPosts();
      await fetchAllPosts();
      
      setError(null);
    } catch (error) {
      console.error("Error reposting immediately:", error);
      setError("Failed to repost immediately. Please try again.");
    }
  };

  const handleRepostSchedule = (post) => {
    setPostToSchedule(post);
    setShowScheduleDialog(true);
  };

  const getSmartScheduledDate = (variantNumber, existingDates = []) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const daysFromToday = variantNumber === 1 ? 0 : variantNumber === 2 ? 3 : 10;
    
    let proposedDate = new Date(today);
    proposedDate.setDate(today.getDate() + daysFromToday);
    
    const maxAttempts = 60;
    let attempts = 0;
    
    while (existingDates.includes(proposedDate.getTime()) && attempts < maxAttempts) {
      proposedDate.setDate(proposedDate.getDate() + 1);
      attempts++;
    }
    
    proposedDate.setHours(12, 0, 0, 0);
    
    return proposedDate;
  };

  const getStatusBadge = (status) => {
    const statusStyles = {
      pending: "social-post-status-pending",
      scheduled: "social-post-status-scheduled",
      published: "social-post-status-published",
      failed: "social-post-status-failed",
      archived: "social-post-status-archived"
    };

    return (
      <span className={`social-post-status-badge ${statusStyles[status] || "social-post-status-unknown"}`} id={`socialPostStatus-${status}`}>
        {status?.charAt(0).toUpperCase() + status?.slice(1)}
      </span>
    );
  };

  const getProviderIcon = (provider) => {
    const iconStyles = "social-provider-icon";
    const iconSize = "16";
    
    switch (provider) {
      case "facebook":
        return (
          <span className={`${iconStyles} social-provider-facebook`} id={`socialProviderIcon-facebook`}>
            <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="currentColor">
              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
            </svg>
          </span>
        );
      case "instagram":
        return (
          <span className={`${iconStyles} social-provider-instagram`} id={`socialProviderIcon-instagram`}>
            <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 0C8.74 0 8.333.015 7.053.072 5.775.132 4.905.333 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.333 4.905.131 5.775.072 7.053.012 8.333 0 8.74 0 12s.015 3.667.072 4.947c.06 1.277.261 2.148.558 2.913.306.788.717 1.459 1.384 2.126.667.666 1.336 1.079 2.126 1.384.766.296 1.636.499 2.913.558C8.333 23.988 8.74 24 12 24s3.667-.015 4.947-.072c1.277-.06 2.148-.262 2.913-.558.788-.306 1.459-.718 2.126-1.384.666-.667 1.079-1.335 1.384-2.126.296-.765.499-1.636.558-2.913.06-1.28.072-1.687.072-4.947s-.015-3.667-.072-4.947c-.06-1.277-.262-2.149-.558-2.913-.306-.789-.718-1.459-1.384-2.126C21.319 1.347 20.651.935 19.86.63c-.765-.297-1.636-.499-2.913-.558C15.667.012 15.26 0 12 0zm0 2.16c3.203 0 3.585.016 4.85.071 1.17.055 1.805.249 2.227.415.562.217.96.477 1.382.896.419.42.679.819.896 1.381.164.422.36 1.057.413 2.227.057 1.266.07 1.646.07 4.85s-.015 3.585-.074 4.85c-.061 1.17-.256 1.805-.421 2.227-.224.562-.479.96-.899 1.382-.419.419-.824.679-1.38.896-.42.164-1.065.36-2.235.413-1.274.057-1.649.07-4.859.07-3.211 0-3.586-.015-4.859-.074-1.171-.061-1.816-.256-2.236-.421-.569-.224-.96-.479-1.379-.899-.421-.419-.69-.824-.9-1.38-.165-.42-.359-1.065-.42-2.235-.045-1.26-.061-1.649-.061-4.844 0-3.196.016-3.586.061-4.861.061-1.17.255-1.814.42-2.234.21-.57.479-.96.9-1.381.419-.419.81-.689 1.379-.898.42-.166 1.051-.361 2.221-.421 1.275-.045 1.65-.06 4.859-.06l.045.03zm0 3.678c-3.405 0-6.162 2.76-6.162 6.162 0 3.405 2.76 6.162 6.162 6.162 3.405 0 6.162-2.76 6.162-6.162 0-3.405-2.76-6.162-6.162-6.162zM12 16c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm7.846-10.405c0 .795-.646 1.44-1.44 1.44-.795 0-1.44-.646-1.44-1.44 0-.794.646-1.439 1.44-1.439.793-.001 1.44.645 1.44 1.439z"/>
            </svg>
          </span>
        );
      case "linkedin":
        return (
          <span className={`${iconStyles} social-provider-linkedin`} id={`socialProviderIcon-linkedin`}>
            <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="currentColor">
              <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
            </svg>
          </span>
        );
      case "x":
      case "twitter":
        return (
          <span className={`${iconStyles} social-provider-twitter`} id={`socialProviderIcon-twitter`}>
            <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="currentColor">
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
            </svg>
          </span>
        );
      default:
        return null;
    }
  };

  const renderPublishInfoBox = (post, variantNumber) => {
    if (post.status !== 'pending') {
      return null;
    }
    
    const postKey = post.providerKey || post.provider;
    const calculatedDate = calculatedScheduleDates[postKey];
    
    if (!calculatedDate) {
      return (
        <div className="publish-info-box" id={`publishInfoBox-${postKey}`}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '8px' }}>
            <circle cx="12" cy="12" r="10"/>
            <polyline points="12 6 12 12 16 14"/>
          </svg>
          Calculating date...
        </div>
      );
    }
    
    const dateStr = calculatedDate.toLocaleDateString('en-US', { 
      month: '2-digit', 
      day: '2-digit', 
      year: '2-digit' 
    });
    
    const variantLabel = variantNumber === 1 ? 'today' : variantNumber === 2 ? 'in 3 days' : 'in 10 days';
    
    return (
      <div className="publish-info-box" id={`publishInfoBox-${postKey}`}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '8px' }}>
          <circle cx="12" cy="12" r="10"/>
          <polyline points="12 6 12 12 16 14"/>
        </svg>
        Default: {dateStr} ({variantLabel})
      </div>
    );
  };

  const groupPostsByPlatform = () => {
    const grouped = {};

    posts.forEach(post => {
      // Check providerPlatform first (newer field), then fall back to provider/providerKey
      const platform = post.providerPlatform || post.provider || post.providerKey?.split('#')[0];

      if (!platform) {
        console.warn('Post missing platform identifier:', post);
        return; // Skip posts without a valid platform
      }

      if (!grouped[platform]) {
        grouped[platform] = [];
      }
      grouped[platform].push(post);
    });
    
    Object.keys(grouped).forEach(platform => {
      grouped[platform].sort((a, b) => {
        const numA = parseInt(a.variantNumber) || 999;
        const numB = parseInt(b.variantNumber) || 999;
        return numA - numB;
      });
    });
    
    return grouped;
  };

  if (loading) {
    return <div className="social-posts-loading" id="socialPostsLoading">Loading social posts...</div>;
  }

  if (error && posts.length === 0) {
    return <div className="social-posts-error" id="socialPostsError">{error}</div>;
  }

  const groupedPosts = groupPostsByPlatform();
  const platforms = Object.keys(groupedPosts).sort();

  return (
    <div className="social-posts-section" id="socialPostsSection">      
      {error && <div className="social-posts-error-message" id="socialPostsErrorMessage">{error}</div>}
      
      <div className="social-posts-container-wrapper" id="socialPostsContainerWrapper">
        <div className="social-posts-container" id="socialPostsContainer">
          {platforms.map(platform => {
            // NEW: Check if platform is connected
            const isConnected = connectionStatuses[platform]?.connected || false;
            
            return (
              <div 
                key={platform} 
                className={`social-posts-column ${!isConnected ? 'social-posts-column-disconnected' : ''}`}
                id={`socialPostsColumn-${platform}`}
              >
                {/* Column Header */}
                <div className="social-posts-column-header" id={`socialPostsColumnHeader-${platform}`}>
                  {getProviderIcon(platform)}
                  <h4 className="social-posts-column-title">
                    {platform.charAt(0).toUpperCase() + platform.slice(1)}
                  </h4>
                  <span className="social-posts-column-count">
                    {groupedPosts[platform].length}
                  </span>
                </div>
                
                {/* NEW: Connection notice for disconnected platforms */}
                {!isConnected && (
                  <div className="social-posts-connection-notice" id={`connectionNotice-${platform}`}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '8px', flexShrink: 0 }}>
                      <circle cx="12" cy="12" r="10"/>
                      <line x1="12" y1="8" x2="12" y2="12"/>
                      <line x1="12" y1="16" x2="12.01" y2="16"/>
                    </svg>
                    <div>
                      <div className="connection-notice-text">
                        {platform.charAt(0).toUpperCase() + platform.slice(1)} not connected
                      </div>
                      {feedId && (
                        <a 
                          href={`/podcast/${feedId}/settings`}
                          className="connection-notice-link"
                          onClick={(e) => {
                            e.preventDefault();
                            window.location.href = `/podcast/${feedId}/settings`;
                          }}
                        >
                          Connect in Feed Settings →
                        </a>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Column Posts */}
                <div className="social-posts-column-content" id={`socialPostsColumnContent-${platform}`}>
                  {groupedPosts[platform].map((post) => (
                    <div 
                      key={post.providerKey || `${post.podcastId}-${post.provider}`}
                      className={`social-post-card ${post.status === 'archived' ? 'social-post-archived' : ''}`}
                      id={`socialPostCard-${post.providerKey || post.provider}`}
                    >
                      {/* Post Header */}
                      <div className="social-post-header" id={`socialPostHeader-${post.providerKey || post.provider}`}>
                        <div className="social-post-header-left" id={`socialPostHeaderLeft-${post.providerKey || post.provider}`}>
                          <span className="social-post-variant" id={`socialPostVariant-${post.providerKey || post.provider}`}>
                            Variant #{post.variantNumber || 1}
                          </span>
                        </div>
                        <div className="social-post-header-right" id={`socialPostHeaderRight-${post.providerKey || post.provider}`}>
                          {getStatusBadge(post.status)}
                        </div>
                      </div>

                      {/* Post Content */}
                      {editingPost?.providerKey === post.providerKey || 
                       (editingPost && editingPost.provider === post.provider && editingPost.podcastId === post.podcastId) ? (
                        // Edit Mode
                        <div className="social-post-edit-container" id={`socialPostEdit-${post.providerKey || post.provider}`}>
                          <textarea
                            value={editContent}
                            onChange={(e) => setEditContent(e.target.value)}
                            className="social-post-edit-textarea"
                            id={`socialPostEditTextarea-${post.providerKey || post.provider}`}
                            rows={6}
                          />
                          <div className="social-post-edit-actions" id={`socialPostEditActions-${post.providerKey || post.provider}`}>
                            <button
                              onClick={() => updatePost(post, 'edit')}
                              className="social-post-save-btn"
                              id={`socialPostSaveBtn-${post.providerKey || post.provider}`}
                            >
                              Save
                            </button>
                            <button
                              onClick={cancelEditing}
                              className="social-post-cancel-btn"
                              id={`socialPostCancelBtn-${post.providerKey || post.provider}`}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        // View Mode
                        <div className="social-post-content-container" id={`socialPostContent-${post.providerKey || post.provider}`}>
                          <p className="social-post-content" id={`socialPostText-${post.providerKey || post.provider}`}>{post.content}</p>
                          
                          <div className="social-post-actions" id={`socialPostActions-${post.providerKey || post.provider}`}>
                            {post.status === 'pending' && (
                              <ApprovalDropdown
                                post={post}
                                onPostNow={handlePostNow}
                                onCustomSchedule={handleCustomSchedule}
                                calculatedDate={calculatedScheduleDates[post.providerKey || post.provider]}
                              />
                            )}
                            
                            {post.status === 'scheduled' && (
                              <button
                                onClick={() => {
                                  setPostToSchedule(post);
                                  setShowScheduleDialog(true);
                                }}
                                className="social-post-reschedule-btn"
                                id={`socialPostRescheduleBtn-${post.providerKey || post.provider}`}
                              >
                                Reschedule
                              </button>
                            )}
                            
                            <AdditionalSocialPostActions
                              post={post}
                              onEdit={startEditing}
                              onReSmack={handleReSmack}
                              onArchive={handleArchive}
                              onUnarchive={handleUnarchive}
                              onRepostNow={handleRepostNow}
                              onRepostSchedule={handleRepostSchedule}
                              isReSmackLoading={reSmackLoading[post.providerKey || `${post.provider}-${post.podcastId}`] || false}
                            />
                          </div>
                        </div>
                      )}
                      {renderPublishInfoBox(post, post.variantNumber || 1)}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      
      {/* Schedule Dialog */}
      <SchedulePostDialog
        isOpen={showScheduleDialog}
        onClose={() => {
          setShowScheduleDialog(false);
          setPostToSchedule(null);
        }}
        onSchedule={handleSchedulePost}
        initialDate={new Date()}
        initialTime="12:00"
      />
    </div>
  );
};

export default SocialPostsView;
